import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import * as api from '../services/api';
import { StatusBadge } from '../components/StatusBadge';
import { EmojiPickerField } from '../components/EmojiPickerField';
import type {
  CharacterPillar,
  HomeActivity,
  LearningLecture,
  Mission
} from '../types/characterPillar';
import type { Badge } from '../types/badge';

const PAGE_SIZE = 10;

type ContentTab = 'missions' | 'lectures' | 'home';

type MissionDraft = {
  key: string;
  name: string;
  description: string;
  instructions: string;
  xp: string;
  badgeId: string;
};

type LectureLinkDraft = {
  key: string;
  url: string;
  label: string;
};

type LectureDraft = {
  key: string;
  title: string;
  description: string;
  xp: string;
  badgeId: string;
  links: LectureLinkDraft[];
  files: File[];
};

type HomeActivityDraft = {
  key: string;
  title: string;
  description: string;
  instructions: string;
  xp: string;
  badgeId: string;
  files: File[];
};

const CONTENT_FILE_ACCEPT =
  'video/*,audio/*,.mp4,.webm,.mov,.avi,.mkv,.mp3,.wav,.ogg,.m4a,.aac,.flac,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,.rtf,image/*';

const CONTENT_FILE_TYPES_HINT =
  'Accepted: video (MP4, WebM, MOV, AVI, MKV), audio (MP3, WAV, OGG, M4A, AAC, FLAC), PDF, Office docs, images, text.';

const DEFAULT_COLOR_THEME = '#4A90E2';
const HEX_COLOR_PATTERN = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/;

function newKey(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function parseXpInput(value: string): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

function normalizeHexColor(value: string): string {
  const trimmed = value.trim();
  if (HEX_COLOR_PATTERN.test(trimmed)) return trimmed;
  return DEFAULT_COLOR_THEME;
}

/** Color inputs need #RRGGBB; expand #RGB when needed. */
function toColorInputValue(hex: string): string {
  const normalized = normalizeHexColor(hex);
  if (normalized.length === 4) {
    const r = normalized[1];
    const g = normalized[2];
    const b = normalized[3];
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  return normalized;
}

function emptyMission(): MissionDraft {
  return { key: newKey(), name: '', description: '', instructions: '', xp: '0', badgeId: '' };
}

function emptyLecture(): LectureDraft {
  return {
    key: newKey(),
    title: '',
    description: '',
    xp: '0',
    badgeId: '',
    links: [],
    files: []
  };
}

function emptyHomeActivity(): HomeActivityDraft {
  return {
    key: newKey(),
    title: '',
    description: '',
    instructions: '',
    xp: '0',
    badgeId: '',
    files: []
  };
}

function emptyLectureLink(): LectureLinkDraft {
  return { key: newKey(), url: '', label: '' };
}

async function attachLectureDraftContent(
  characterPillarId: string,
  lectureId: string,
  draft: Pick<LectureDraft, 'files' | 'links'>
): Promise<void> {
  for (const file of draft.files) {
    await api.uploadLectureFile(characterPillarId, lectureId, file);
  }
  for (const link of draft.links) {
    const url = link.url.trim();
    if (!url) continue;
    await api.attachLectureLink(characterPillarId, lectureId, {
      url,
      label: link.label.trim() || undefined,
      linkType: 'video'
    });
  }
}

async function attachHomeActivityDraftFiles(
  characterPillarId: string,
  homeActivityId: string,
  files: File[]
): Promise<void> {
  for (const file of files) {
    await api.uploadHomeActivityFile(characterPillarId, homeActivityId, file);
  }
}

export function CharacterPillarsPage() {
  const createModalTitleId = useId();
  const [pillars, setPillars] = useState<CharacterPillar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [activeOnly, setActiveOnly] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<CharacterPillar | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editColorTheme, setEditColorTheme] = useState(DEFAULT_COLOR_THEME);
  const [editIcon, setEditIcon] = useState('');
  const [editSortOrder, setEditSortOrder] = useState('0');
  const [saving, setSaving] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createColorTheme, setCreateColorTheme] = useState(DEFAULT_COLOR_THEME);
  const [createIcon, setCreateIcon] = useState('');
  const [createSortOrder, setCreateSortOrder] = useState('0');
  const [createMissions, setCreateMissions] = useState<MissionDraft[]>([]);
  const [createLectures, setCreateLectures] = useState<LectureDraft[]>([]);
  const [createHomeActivities, setCreateHomeActivities] = useState<HomeActivityDraft[]>([]);
  const [createModalTab, setCreateModalTab] = useState<ContentTab>('missions');
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const [contentTab, setContentTab] = useState<ContentTab>('missions');
  const [missions, setMissions] = useState<Mission[]>([]);
  const [lectures, setLectures] = useState<LearningLecture[]>([]);
  const [homeActivities, setHomeActivities] = useState<HomeActivity[]>([]);
  const [contentLoading, setContentLoading] = useState(false);

  const [childName, setChildName] = useState('');
  const [childDescription, setChildDescription] = useState('');
  const [childInstructions, setChildInstructions] = useState('');
  const [childXp, setChildXp] = useState('0');
  const [childBadgeId, setChildBadgeId] = useState('');
  const [childVideoLink, setChildVideoLink] = useState('');
  const [childFiles, setChildFiles] = useState<File[]>([]);
  const [creatingChild, setCreatingChild] = useState(false);
  const [childBusyId, setChildBusyId] = useState<string | null>(null);
  const [lectureLinkInputs, setLectureLinkInputs] = useState<Record<string, string>>({});
  const [xpDrafts, setXpDrafts] = useState<Record<string, string>>({});
  const [badgeDrafts, setBadgeDrafts] = useState<Record<string, string>>({});
  const [badges, setBadges] = useState<Badge[]>([]);
  const loadPillars = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [items, badgeItems] = await Promise.all([
        api.listCharacterPillars({
          activeOnly: activeOnly || undefined
        }),
        api.listBadges({ activeOnly: true })
      ]);
      setPillars(items);
      setBadges(badgeItems);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load character pillars');
    } finally {
      setLoading(false);
    }
  }, [activeOnly]);

  useEffect(() => {
    void loadPillars();
  }, [loadPillars]);

  const loadContent = useCallback(async (pillarId: string) => {
    setContentLoading(true);
    try {
      const [missionItems, lectureItems, homeItems] = await Promise.all([
        api.listMissions(pillarId),
        api.listLearningLectures(pillarId),
        api.listHomeActivities(pillarId)
      ]);
      setMissions(missionItems);
      setLectures(lectureItems);
      setHomeActivities(homeItems);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pillar content');
    } finally {
      setContentLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      setMissions([]);
      setLectures([]);
      setHomeActivities([]);
      return;
    }

    let cancelled = false;

    async function loadDetail() {
      setDetailLoading(true);
      try {
        const pillar = await api.getCharacterPillar(selectedId!);
        if (!cancelled) {
          setSelected(pillar);
          setEditName(pillar.name);
          setEditDescription(pillar.description ?? '');
          setEditColorTheme(pillar.colorTheme || DEFAULT_COLOR_THEME);
          setEditIcon(pillar.icon ?? '');
          setEditSortOrder(String(pillar.sortOrder));
          await loadContent(pillar.id);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load character pillar');
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    }

    void loadDetail();
    return () => {
      cancelled = true;
    };
  }, [selectedId, loadContent]);

  const totalPages = Math.max(1, Math.ceil(pillars.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = pillars.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const rangeStart = pillars.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, pillars.length);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  useEffect(() => {
    if (!showCreate) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setShowCreate(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showCreate]);

  function resetCreateForm() {
    setCreateName('');
    setCreateDescription('');
    setCreateColorTheme(DEFAULT_COLOR_THEME);
    setCreateIcon('');
    setCreateSortOrder('0');
    setCreateMissions([]);
    setCreateLectures([]);
    setCreateHomeActivities([]);
    setCreateModalTab('missions');
    setCreateError(null);
  }

  function openCreateModal() {
    resetCreateForm();
    setError(null);
    setSuccess(null);
    setShowCreate(true);
  }

  function closeCreateModal() {
    if (creating) return;
    setShowCreate(false);
    setCreateError(null);
  }

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setCreating(true);
    setCreateError(null);
    setError(null);
    setSuccess(null);

    const missionsPayload = createMissions
      .map((item) => ({
        name: item.name.trim(),
        description: item.description.trim() || undefined,
        instructions: item.instructions.trim() || undefined,
        xp: parseXpInput(item.xp),
        badgeId: item.badgeId || null
      }))
      .filter((item) => item.name);

    const lectureDrafts = createLectures.filter((item) => item.title.trim());
    const lecturesPayload = lectureDrafts.map((item) => ({
      title: item.title.trim(),
      description: item.description.trim() || undefined,
      xp: parseXpInput(item.xp),
      badgeId: item.badgeId || null
    }));

    const homeDrafts = createHomeActivities.filter((item) => item.title.trim());
    const homePayload = homeDrafts.map((item) => ({
      title: item.title.trim(),
      description: item.description.trim() || undefined,
      instructions: item.instructions.trim() || undefined,
      xp: parseXpInput(item.xp),
      badgeId: item.badgeId || null
    }));

    const colorTheme = createColorTheme.trim();
    const icon = createIcon.trim();
    if (!HEX_COLOR_PATTERN.test(colorTheme)) {
      setCreateError('Colour theme must be a hex colour (#RGB or #RRGGBB).');
      setCreating(false);
      return;
    }
    if (!icon) {
      setCreateError('Icon or emoji is required.');
      setCreating(false);
      return;
    }

    try {
      const result = await api.createCharacterPillar({
        name: createName.trim(),
        description: createDescription.trim() || undefined,
        colorTheme,
        icon,
        sortOrder: Number(createSortOrder) || 0,
        missions: missionsPayload,
        learningLectures: lecturesPayload,
        homeActivities: homePayload
      });

      let lecturesWithContent = result.learningLectures;
      for (let index = 0; index < lectureDrafts.length; index += 1) {
        const draft = lectureDrafts[index];
        const lecture = result.learningLectures[index];
        if (!draft || !lecture) continue;
        if (draft.files.length === 0 && draft.links.every((link) => !link.url.trim())) {
          continue;
        }
        await attachLectureDraftContent(result.pillar.id, lecture.id, draft);
      }

      if (
        lectureDrafts.some(
          (draft) => draft.files.length > 0 || draft.links.some((link) => link.url.trim())
        )
      ) {
        lecturesWithContent = await api.listLearningLectures(result.pillar.id);
      }

      let homesWithContent = result.homeActivities;
      for (let index = 0; index < homeDrafts.length; index += 1) {
        const draft = homeDrafts[index];
        const activity = result.homeActivities[index];
        if (!draft || !activity || draft.files.length === 0) continue;
        await attachHomeActivityDraftFiles(result.pillar.id, activity.id, draft.files);
      }

      if (homeDrafts.some((draft) => draft.files.length > 0)) {
        homesWithContent = await api.listHomeActivities(result.pillar.id);
      }

      setPillars((current) => [result.pillar, ...current]);
      setShowCreate(false);
      resetCreateForm();
      setSelectedId(result.pillar.id);
      setMissions(result.missions);
      setLectures(lecturesWithContent);
      setHomeActivities(homesWithContent);
      setSuccess(
        `Created pillar ${result.pillar.name} with ${result.missions.length} mission(s), ${lecturesWithContent.length} lecture(s), and ${homesWithContent.length} home activity(ies).`
      );
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create character pillar');
    } finally {
      setCreating(false);
    }
  }

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    const colorTheme = editColorTheme.trim();
    const icon = editIcon.trim();
    if (!HEX_COLOR_PATTERN.test(colorTheme)) {
      setError('Colour theme must be a hex colour (#RGB or #RRGGBB).');
      setSaving(false);
      return;
    }
    if (!icon) {
      setError('Icon or emoji is required.');
      setSaving(false);
      return;
    }

    try {
      const updated = await api.updateCharacterPillar(selected.id, {
        name: editName.trim(),
        description: editDescription.trim() || null,
        colorTheme,
        icon,
        sortOrder: Number(editSortOrder) || 0
      });
      setSelected(updated);
      setEditColorTheme(updated.colorTheme);
      setEditIcon(updated.icon);
      setPillars((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      setSuccess('Character pillar saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save character pillar');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(pillar: CharacterPillar) {
    setBusyId(pillar.id);
    setError(null);
    setSuccess(null);
    try {
      const updated = pillar.isActive
        ? await api.deactivateCharacterPillar(pillar.id)
        : await api.reactivateCharacterPillar(pillar.id);
      setPillars((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      if (selected?.id === updated.id) setSelected(updated);
      setSuccess(`${updated.name} is now ${updated.isActive ? 'active' : 'inactive'}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update pillar status');
    } finally {
      setBusyId(null);
    }
  }

  async function handleCreateChild(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setCreatingChild(true);
    setError(null);
    setSuccess(null);
    try {
      if (contentTab === 'missions') {
        const created = await api.createMission(selected.id, {
          name: childName.trim(),
          description: childDescription.trim() || undefined,
          instructions: childInstructions.trim() || undefined,
          xp: parseXpInput(childXp),
          badgeId: childBadgeId || null
        });
        setMissions((current) => [created, ...current]);
        setSuccess(`Created mission ${created.name}.`);
      } else if (contentTab === 'lectures') {
        let created = await api.createLearningLecture(selected.id, {
          title: childName.trim(),
          description: childDescription.trim() || undefined,
          xp: parseXpInput(childXp),
          badgeId: childBadgeId || null
        });
        const linkUrl = childVideoLink.trim();
        if (childFiles.length > 0 || linkUrl) {
          await attachLectureDraftContent(selected.id, created.id, {
            files: childFiles,
            links: linkUrl
              ? [{ key: newKey(), url: linkUrl, label: '' }]
              : []
          });
          created = (await api.listLearningLectures(selected.id)).find(
            (item) => item.id === created.id
          ) ?? created;
        }
        setLectures((current) => [created, ...current.filter((item) => item.id !== created.id)]);
        setSuccess(`Created lecture ${created.title}.`);
      } else {
        let created = await api.createHomeActivity(selected.id, {
          title: childName.trim(),
          description: childDescription.trim() || undefined,
          instructions: childInstructions.trim() || undefined,
          xp: parseXpInput(childXp),
          badgeId: childBadgeId || null
        });
        if (childFiles.length > 0) {
          await attachHomeActivityDraftFiles(selected.id, created.id, childFiles);
          created =
            (await api.listHomeActivities(selected.id)).find((item) => item.id === created.id) ??
            created;
        }
        setHomeActivities((current) => [created, ...current.filter((item) => item.id !== created.id)]);
        setSuccess(`Created home activity ${created.title}.`);
      }
      setChildName('');
      setChildDescription('');
      setChildInstructions('');
      setChildXp('0');
      setChildBadgeId('');
      setChildVideoLink('');
      setChildFiles([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create content item');
    } finally {
      setCreatingChild(false);
    }
  }

  async function handleUploadLectureFiles(lectureId: string, files: FileList | null) {
    if (!selected || !files?.length) return;
    setChildBusyId(lectureId);
    setError(null);
    try {
      let updated: LearningLecture | null = null;
      for (const file of Array.from(files)) {
        updated = await api.uploadLectureFile(selected.id, lectureId, file);
      }
      if (updated) {
        setLectures((current) =>
          current.map((item) => (item.id === updated!.id ? updated! : item))
        );
        setSuccess(`Uploaded file(s) to ${updated.title}.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload lecture file');
    } finally {
      setChildBusyId(null);
    }
  }

  async function handleUploadHomeActivityFiles(homeActivityId: string, files: FileList | null) {
    if (!selected || !files?.length) return;
    setChildBusyId(homeActivityId);
    setError(null);
    try {
      let updated: HomeActivity | null = null;
      for (const file of Array.from(files)) {
        updated = await api.uploadHomeActivityFile(selected.id, homeActivityId, file);
      }
      if (updated) {
        setHomeActivities((current) =>
          current.map((item) => (item.id === updated!.id ? updated! : item))
        );
        setSuccess(`Uploaded file(s) to ${updated.title}.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload home activity file');
    } finally {
      setChildBusyId(null);
    }
  }

  async function handleRemoveHomeActivityFile(homeActivityId: string, fileId: string) {
    if (!selected) return;
    setChildBusyId(homeActivityId);
    setError(null);
    try {
      const updated = await api.removeHomeActivityContent(selected.id, homeActivityId, fileId);
      setHomeActivities((current) =>
        current.map((item) => (item.id === updated.id ? updated : item))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove home activity file');
    } finally {
      setChildBusyId(null);
    }
  }

  async function handleAttachLectureLink(lectureId: string) {
    if (!selected) return;
    const url = (lectureLinkInputs[lectureId] ?? '').trim();
    if (!url) return;
    setChildBusyId(lectureId);
    setError(null);
    try {
      const updated = await api.attachLectureLink(selected.id, lectureId, {
        url,
        linkType: 'video'
      });
      setLectures((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      setLectureLinkInputs((current) => ({ ...current, [lectureId]: '' }));
      setSuccess(`Attached link to ${updated.title}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to attach lecture link');
    } finally {
      setChildBusyId(null);
    }
  }

  async function handleRemoveLectureFile(lectureId: string, fileId: string) {
    if (!selected) return;
    setChildBusyId(lectureId);
    setError(null);
    try {
      const updated = await api.removeLectureContent(selected.id, lectureId, fileId);
      setLectures((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove lecture file');
    } finally {
      setChildBusyId(null);
    }
  }

  async function handleRemoveLectureLink(lectureId: string, linkId: string) {
    if (!selected) return;
    setChildBusyId(lectureId);
    setError(null);
    try {
      const updated = await api.removeLectureLink(selected.id, lectureId, linkId);
      setLectures((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove lecture link');
    } finally {
      setChildBusyId(null);
    }
  }

  async function handleSaveChildXp(kind: ContentTab, id: string, currentXp: number) {
    if (!selected) return;
    const nextXp = parseXpInput(xpDrafts[id] ?? String(currentXp));
    if (nextXp === currentXp) return;

    setChildBusyId(id);
    setError(null);
    setSuccess(null);
    try {
      if (kind === 'missions') {
        const updated = await api.updateMission(selected.id, id, { xp: nextXp });
        setMissions((current) => current.map((item) => (item.id === updated.id ? updated : item)));
        setSuccess(`Updated XP for ${updated.name}.`);
      } else if (kind === 'lectures') {
        const updated = await api.updateLearningLecture(selected.id, id, { xp: nextXp });
        setLectures((current) => current.map((item) => (item.id === updated.id ? updated : item)));
        setSuccess(`Updated XP for ${updated.title}.`);
      } else {
        const updated = await api.updateHomeActivity(selected.id, id, { xp: nextXp });
        setHomeActivities((current) =>
          current.map((item) => (item.id === updated.id ? updated : item))
        );
        setSuccess(`Updated XP for ${updated.title}.`);
      }
      setXpDrafts((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update XP');
    } finally {
      setChildBusyId(null);
    }
  }

  async function handleSaveChildBadge(kind: ContentTab, id: string, currentBadgeId?: string | null) {
    if (!selected) return;
    const nextBadgeId = badgeDrafts[id] ?? currentBadgeId ?? '';
    const normalizedCurrent = currentBadgeId ?? '';
    if (nextBadgeId === normalizedCurrent) return;

    setChildBusyId(id);
    setError(null);
    setSuccess(null);
    try {
      const payload = { badgeId: nextBadgeId || null };
      if (kind === 'missions') {
        const updated = await api.updateMission(selected.id, id, payload);
        setMissions((current) => current.map((item) => (item.id === updated.id ? updated : item)));
        setSuccess(`Updated badge for ${updated.name}.`);
      } else if (kind === 'lectures') {
        const updated = await api.updateLearningLecture(selected.id, id, payload);
        setLectures((current) => current.map((item) => (item.id === updated.id ? updated : item)));
        setSuccess(`Updated badge for ${updated.title}.`);
      } else {
        const updated = await api.updateHomeActivity(selected.id, id, payload);
        setHomeActivities((current) =>
          current.map((item) => (item.id === updated.id ? updated : item))
        );
        setSuccess(`Updated badge for ${updated.title}.`);
      }
      setBadgeDrafts((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update badge');
    } finally {
      setChildBusyId(null);
    }
  }

  async function handleToggleChildActive(kind: ContentTab, id: string, isActive: boolean) {
    if (!selected) return;
    setChildBusyId(id);
    setError(null);
    try {
      if (kind === 'missions') {
        const updated = isActive
          ? await api.deactivateMission(selected.id, id)
          : await api.reactivateMission(selected.id, id);
        setMissions((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      } else if (kind === 'lectures') {
        const updated = isActive
          ? await api.deactivateLearningLecture(selected.id, id)
          : await api.reactivateLearningLecture(selected.id, id);
        setLectures((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      } else {
        const updated = isActive
          ? await api.deactivateHomeActivity(selected.id, id)
          : await api.reactivateHomeActivity(selected.id, id);
        setHomeActivities((current) =>
          current.map((item) => (item.id === updated.id ? updated : item))
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update content status');
    } finally {
      setChildBusyId(null);
    }
  }

  const childLabel =
    contentTab === 'missions'
      ? 'mission'
      : contentTab === 'lectures'
        ? 'lecture'
        : 'home activity';

  return (
    <section className="admin-panel schools-page">
      <div className="panel-heading">
        <div>
          <h2>Character Pillars</h2>
          <p>
            Define platform character pillars and their missions, learning lectures, and home
            activities — including XP rewards awarded when each item is marked done.
          </p>
        </div>
        <span className="panel-meta">{pillars.length} total</span>
      </div>

      <div className="filter-bar classes-toolbar">
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => {
              setPage(1);
              setActiveOnly(e.target.checked);
            }}
          />
          Active only
        </label>
        <button type="button" className="primary-btn" onClick={() => void loadPillars()}>
          Refresh
        </button>
        <button type="button" className="ghost-btn" onClick={openCreateModal}>
          New pillar
        </button>
      </div>

      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="form-success" role="status">
          {success}
        </p>
      ) : null}

      {loading ? <p className="muted">Loading character pillars…</p> : null}
      {!loading && pillars.length === 0 ? (
        <p className="muted">No character pillars yet. Use New pillar to create one.</p>
      ) : null}

      {!loading && pillars.length > 0 ? (
        <div className="schools-split">
          <div className="school-list-panel">
            <div className="table-wrap school-list-table">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Pillar</th>
                    <th>Order</th>
                    <th>Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((pillar) => (
                    <tr
                      key={pillar.id}
                      className={`school-row${selectedId === pillar.id ? ' is-selected' : ''}`}
                      onClick={() => setSelectedId(pillar.id)}
                    >
                      <td>
                        <div className="pillar-name-cell">
                          <span
                            className="pillar-icon-badge"
                            style={{ backgroundColor: pillar.colorTheme || DEFAULT_COLOR_THEME }}
                            aria-hidden
                          >
                            {pillar.icon || '•'}
                          </span>
                          <span>
                            <strong>{pillar.name}</strong>
                            {pillar.description ? (
                              <span className="cell-sub">{pillar.description}</span>
                            ) : null}
                          </span>
                        </div>
                      </td>
                      <td>{pillar.sortOrder}</td>
                      <td>
                        <StatusBadge status={pillar.isActive ? 'active' : 'suspended'} />
                      </td>
                      <td>
                        <div className="action-row" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="ghost-btn compact"
                            disabled={busyId === pillar.id}
                            onClick={() => void handleToggleActive(pillar)}
                          >
                            {pillar.isActive ? 'Deactivate' : 'Reactivate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div
              className="school-list-pager"
              role="navigation"
              aria-label="Character pillar list pagination"
            >
              <span className="school-list-pager-range">
                {rangeStart}-{rangeEnd} of {pillars.length}
              </span>
              <button
                type="button"
                className="pager-chevron"
                aria-label="Previous page"
                disabled={safePage <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                ‹
              </button>
              <button
                type="button"
                className="pager-chevron"
                aria-label="Next page"
                disabled={safePage >= totalPages}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >
                ›
              </button>
            </div>
          </div>

          <aside className="school-detail settings-section">
            {!selectedId ? (
              <div className="settings-section-body">
                <p className="muted">Click a pillar row to view and manage its content.</p>
              </div>
            ) : null}

            {selectedId && detailLoading ? (
              <div className="settings-section-body">
                <p className="muted">Loading pillar…</p>
              </div>
            ) : null}

            {selected && !detailLoading ? (
              <>
                <div className="settings-section-header">
                  <h3>
                    <span
                      className="pillar-icon-badge pillar-icon-badge-lg"
                      style={{ backgroundColor: selected.colorTheme || DEFAULT_COLOR_THEME }}
                      aria-hidden
                    >
                      {selected.icon || '•'}
                    </span>
                    {selected.name}
                  </h3>
                  <p className="school-detail-meta">
                    <StatusBadge status={selected.isActive ? 'active' : 'suspended'} />
                    <span>Order {selected.sortOrder}</span>
                  </p>
                </div>
                <div className="settings-section-body">
                  <form className="stacked-form" onSubmit={handleSave}>
                    <label>
                      Name
                      <input
                        required
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                      />
                    </label>
                    <label>
                      Icon / emoji
                      <EmojiPickerField
                        required
                        value={editIcon}
                        onChange={setEditIcon}
                        placeholder="Select emoji"
                      />
                    </label>
                    <label>
                      Colour theme
                      <span className="color-theme-field">
                        <input
                          type="color"
                          aria-label="Pick colour theme"
                          value={toColorInputValue(editColorTheme)}
                          onChange={(e) => setEditColorTheme(e.target.value.toUpperCase())}
                        />
                        <input
                          required
                          value={editColorTheme}
                          onChange={(e) => setEditColorTheme(e.target.value)}
                          placeholder="#4A90E2"
                          pattern="^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$"
                          title="Hex colour #RGB or #RRGGBB"
                        />
                      </span>
                    </label>
                    <label>
                      Sort order
                      <input
                        type="number"
                        min={0}
                        value={editSortOrder}
                        onChange={(e) => setEditSortOrder(e.target.value)}
                      />
                    </label>
                    <label>
                      Description
                      <textarea
                        rows={3}
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                      />
                    </label>
                    <div className="form-actions">
                      <button type="submit" className="primary-btn" disabled={saving}>
                        {saving ? 'Saving…' : 'Save pillar'}
                      </button>
                      <button
                        type="button"
                        className="ghost-btn"
                        onClick={() => setSelectedId(null)}
                      >
                        Close
                      </button>
                    </div>
                  </form>

                  <div className="pillar-content-block">
                    <div className="tab-bar" role="tablist" aria-label="Pillar content">
                      <button
                        type="button"
                        className={`tab-btn${contentTab === 'missions' ? ' active' : ''}`}
                        onClick={() => setContentTab('missions')}
                      >
                        Missions ({missions.length})
                      </button>
                      <button
                        type="button"
                        className={`tab-btn${contentTab === 'lectures' ? ' active' : ''}`}
                        onClick={() => setContentTab('lectures')}
                      >
                        Lectures ({lectures.length})
                      </button>
                      <button
                        type="button"
                        className={`tab-btn${contentTab === 'home' ? ' active' : ''}`}
                        onClick={() => setContentTab('home')}
                      >
                        Home ({homeActivities.length})
                      </button>
                    </div>

                    <form className="stacked-form pillar-child-form" onSubmit={handleCreateChild}>
                      <label>
                        {contentTab === 'missions' ? 'Mission name' : 'Title'}
                        <input
                          required
                          value={childName}
                          onChange={(e) => setChildName(e.target.value)}
                        />
                      </label>
                      <label>
                        Description
                        <textarea
                          rows={2}
                          value={childDescription}
                          onChange={(e) => setChildDescription(e.target.value)}
                        />
                      </label>
                      {contentTab === 'missions' ? (
                        <label>
                          Instructions
                          <textarea
                            rows={2}
                            value={childInstructions}
                            onChange={(e) => setChildInstructions(e.target.value)}
                          />
                        </label>
                      ) : null}
                      {contentTab === 'home' ? (
                        <>
                          <label>
                            Instructions
                            <textarea
                              rows={2}
                              value={childInstructions}
                              onChange={(e) => setChildInstructions(e.target.value)}
                            />
                          </label>
                          <label>
                            Files
                            <input
                              type="file"
                              multiple
                              accept={CONTENT_FILE_ACCEPT}
                              onChange={(e) => {
                                setChildFiles(Array.from(e.target.files ?? []));
                                e.target.value = '';
                              }}
                            />
                            <span className="field-hint">{CONTENT_FILE_TYPES_HINT}</span>
                          </label>
                          {childFiles.length > 0 ? (
                            <ul className="lecture-pending-list">
                              {childFiles.map((file, index) => (
                                <li key={`${file.name}-${file.size}-${index}`}>
                                  <span>
                                    {file.name}{' '}
                                    <span className="cell-sub">
                                      ({Math.round(file.size / 1024)} KB)
                                    </span>
                                  </span>
                                  <button
                                    type="button"
                                    className="ghost-btn compact"
                                    onClick={() =>
                                      setChildFiles((current) =>
                                        current.filter((_, i) => i !== index)
                                      )
                                    }
                                  >
                                    Remove
                                  </button>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </>
                      ) : null}
                      {contentTab === 'lectures' ? (
                        <>
                          <label>
                            Video link
                            <input
                              type="url"
                              value={childVideoLink}
                              onChange={(e) => setChildVideoLink(e.target.value)}
                              placeholder="https://…"
                            />
                          </label>
                          <label>
                            Files
                            <input
                              type="file"
                              multiple
                              accept={CONTENT_FILE_ACCEPT}
                              onChange={(e) => {
                                setChildFiles(Array.from(e.target.files ?? []));
                                e.target.value = '';
                              }}
                            />
                            <span className="field-hint">{CONTENT_FILE_TYPES_HINT}</span>
                          </label>
                          {childFiles.length > 0 ? (
                            <ul className="lecture-pending-list">
                              {childFiles.map((file, index) => (
                                <li key={`${file.name}-${file.size}-${index}`}>
                                  <span>
                                    {file.name}{' '}
                                    <span className="cell-sub">
                                      ({Math.round(file.size / 1024)} KB)
                                    </span>
                                  </span>
                                  <button
                                    type="button"
                                    className="ghost-btn compact"
                                    onClick={() =>
                                      setChildFiles((current) =>
                                        current.filter((_, i) => i !== index)
                                      )
                                    }
                                  >
                                    Remove
                                  </button>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </>
                      ) : null}
                      <label>
                        XP reward
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={childXp}
                          onChange={(e) => setChildXp(e.target.value)}
                        />
                      </label>
                      <label>
                        Badge (optional)
                        <select
                          value={childBadgeId}
                          onChange={(e) => setChildBadgeId(e.target.value)}
                        >
                          <option value="">No badge</option>
                          {badges.map((badge) => (
                            <option key={badge.id} value={badge.id}>
                              {badge.icon ? `${badge.icon} ` : ''}
                              {badge.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div className="form-actions">
                        <button type="submit" className="primary-btn" disabled={creatingChild}>
                          {creatingChild ? 'Creating…' : `Add ${childLabel}`}
                        </button>
                      </div>
                    </form>

                    {contentLoading ? <p className="muted">Loading content…</p> : null}

                    {!contentLoading && contentTab === 'missions' ? (
                      <ul className="pillar-child-list">
                        {missions.length === 0 ? (
                          <li className="muted">No missions yet.</li>
                        ) : (
                          missions.map((mission) => (
                            <li key={mission.id}>
                              <div>
                                <strong>{mission.name}</strong>
                                <span className="cell-sub">+{mission.xp} XP</span>
                                <StatusBadge
                                  status={mission.isActive ? 'active' : 'suspended'}
                                />
                              </div>
                              <div className="pillar-child-actions">
                                <label className="xp-edit-field">
                                  XP
                                  <input
                                    type="number"
                                    min={0}
                                    step={1}
                                    value={xpDrafts[mission.id] ?? String(mission.xp)}
                                    onChange={(e) =>
                                      setXpDrafts((current) => ({
                                        ...current,
                                        [mission.id]: e.target.value
                                      }))
                                    }
                                  />
                                </label>
                                <button
                                  type="button"
                                  className="ghost-btn compact"
                                  disabled={
                                    childBusyId === mission.id ||
                                    parseXpInput(xpDrafts[mission.id] ?? String(mission.xp)) ===
                                      mission.xp
                                  }
                                  onClick={() =>
                                    void handleSaveChildXp('missions', mission.id, mission.xp)
                                  }
                                >
                                  Save XP
                                </button>
                                <label className="xp-edit-field">
                                  Badge
                                  <select
                                    value={
                                      badgeDrafts[mission.id] ?? mission.badgeId ?? ''
                                    }
                                    onChange={(e) =>
                                      setBadgeDrafts((current) => ({
                                        ...current,
                                        [mission.id]: e.target.value
                                      }))
                                    }
                                  >
                                    <option value="">No badge</option>
                                    {badges.map((badge) => (
                                      <option key={badge.id} value={badge.id}>
                                        {badge.icon ? `${badge.icon} ` : ''}
                                        {badge.name}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <button
                                  type="button"
                                  className="ghost-btn compact"
                                  disabled={
                                    childBusyId === mission.id ||
                                    (badgeDrafts[mission.id] ?? mission.badgeId ?? '') ===
                                      (mission.badgeId ?? '')
                                  }
                                  onClick={() =>
                                    void handleSaveChildBadge(
                                      'missions',
                                      mission.id,
                                      mission.badgeId
                                    )
                                  }
                                >
                                  Save badge
                                </button>
                                <button
                                  type="button"
                                  className="ghost-btn compact"
                                  disabled={childBusyId === mission.id}
                                  onClick={() =>
                                    void handleToggleChildActive(
                                      'missions',
                                      mission.id,
                                      mission.isActive
                                    )
                                  }
                                >
                                  {mission.isActive ? 'Deactivate' : 'Reactivate'}
                                </button>
                              </div>
                            </li>
                          ))
                        )}
                      </ul>
                    ) : null}

                    {!contentLoading && contentTab === 'lectures' ? (
                      <ul className="pillar-child-list">
                        {lectures.length === 0 ? (
                          <li className="muted">No learning lectures yet.</li>
                        ) : (
                          lectures.map((lecture) => (
                            <li key={lecture.id} className="pillar-child-lecture">
                              <div className="pillar-child-lecture-header">
                                <div>
                                  <strong>{lecture.title}</strong>
                                  <span className="cell-sub">
                                    +{lecture.xp} XP · {lecture.contentFiles.length} file
                                    {lecture.contentFiles.length === 1 ? '' : 's'}
                                    {lecture.contentLinks.length > 0
                                      ? ` · ${lecture.contentLinks.length} link${
                                          lecture.contentLinks.length === 1 ? '' : 's'
                                        }`
                                      : ''}
                                  </span>
                                  <StatusBadge
                                    status={lecture.isActive ? 'active' : 'suspended'}
                                  />
                                </div>
                                <div className="pillar-child-actions">
                                  <label className="xp-edit-field">
                                    XP
                                    <input
                                      type="number"
                                      min={0}
                                      step={1}
                                      value={xpDrafts[lecture.id] ?? String(lecture.xp)}
                                      onChange={(e) =>
                                        setXpDrafts((current) => ({
                                          ...current,
                                          [lecture.id]: e.target.value
                                        }))
                                      }
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    className="ghost-btn compact"
                                    disabled={
                                      childBusyId === lecture.id ||
                                      parseXpInput(xpDrafts[lecture.id] ?? String(lecture.xp)) ===
                                        lecture.xp
                                    }
                                    onClick={() =>
                                      void handleSaveChildXp('lectures', lecture.id, lecture.xp)
                                    }
                                  >
                                    Save XP
                                  </button>
                                  <label className="xp-edit-field">
                                    Badge
                                    <select
                                      value={
                                        badgeDrafts[lecture.id] ?? lecture.badgeId ?? ''
                                      }
                                      onChange={(e) =>
                                        setBadgeDrafts((current) => ({
                                          ...current,
                                          [lecture.id]: e.target.value
                                        }))
                                      }
                                    >
                                      <option value="">No badge</option>
                                      {badges.map((badge) => (
                                        <option key={badge.id} value={badge.id}>
                                          {badge.icon ? `${badge.icon} ` : ''}
                                          {badge.name}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <button
                                    type="button"
                                    className="ghost-btn compact"
                                    disabled={
                                      childBusyId === lecture.id ||
                                      (badgeDrafts[lecture.id] ?? lecture.badgeId ?? '') ===
                                        (lecture.badgeId ?? '')
                                    }
                                    onClick={() =>
                                      void handleSaveChildBadge(
                                        'lectures',
                                        lecture.id,
                                        lecture.badgeId
                                      )
                                    }
                                  >
                                    Save badge
                                  </button>
                                  <button
                                    type="button"
                                    className="ghost-btn compact"
                                    disabled={childBusyId === lecture.id}
                                    onClick={() =>
                                      void handleToggleChildActive(
                                        'lectures',
                                        lecture.id,
                                        lecture.isActive
                                      )
                                    }
                                  >
                                    {lecture.isActive ? 'Deactivate' : 'Reactivate'}
                                  </button>
                                </div>
                              </div>

                              {(lecture.contentFiles.length > 0 ||
                                lecture.contentLinks.length > 0) && (
                                <ul className="lecture-content-list">
                                  {lecture.contentFiles.map((file) => (
                                    <li key={file.fileId}>
                                      <span>
                                        {file.originalFilename}{' '}
                                        <span className="cell-sub">({file.fileType})</span>
                                      </span>
                                      <button
                                        type="button"
                                        className="ghost-btn compact"
                                        disabled={childBusyId === lecture.id}
                                        onClick={() =>
                                          void handleRemoveLectureFile(lecture.id, file.fileId)
                                        }
                                      >
                                        Remove
                                      </button>
                                    </li>
                                  ))}
                                  {lecture.contentLinks.map((link) => (
                                    <li key={link.linkId}>
                                      <a
                                        href={link.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="lecture-link"
                                      >
                                        {link.label || link.url}
                                      </a>
                                      <button
                                        type="button"
                                        className="ghost-btn compact"
                                        disabled={childBusyId === lecture.id}
                                        onClick={() =>
                                          void handleRemoveLectureLink(lecture.id, link.linkId)
                                        }
                                      >
                                        Remove
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}

                              <div className="lecture-content-actions">
                                <label className="lecture-file-picker">
                                  Upload files
                                  <input
                                    type="file"
                                    multiple
                                    accept={CONTENT_FILE_ACCEPT}
                                    disabled={childBusyId === lecture.id}
                                    onChange={(e) => {
                                      void handleUploadLectureFiles(lecture.id, e.target.files);
                                      e.target.value = '';
                                    }}
                                  />
                                  <span className="field-hint">{CONTENT_FILE_TYPES_HINT}</span>
                                </label>
                                <div className="lecture-link-row">
                                  <input
                                    type="url"
                                    value={lectureLinkInputs[lecture.id] ?? ''}
                                    onChange={(e) =>
                                      setLectureLinkInputs((current) => ({
                                        ...current,
                                        [lecture.id]: e.target.value
                                      }))
                                    }
                                    placeholder="Video or document URL"
                                    disabled={childBusyId === lecture.id}
                                  />
                                  <button
                                    type="button"
                                    className="ghost-btn compact"
                                    disabled={
                                      childBusyId === lecture.id ||
                                      !(lectureLinkInputs[lecture.id] ?? '').trim()
                                    }
                                    onClick={() => void handleAttachLectureLink(lecture.id)}
                                  >
                                    Add link
                                  </button>
                                </div>
                              </div>
                            </li>
                          ))
                        )}
                      </ul>
                    ) : null}

                    {!contentLoading && contentTab === 'home' ? (
                      <ul className="pillar-child-list">
                        {homeActivities.length === 0 ? (
                          <li className="muted">No home activities yet.</li>
                        ) : (
                          homeActivities.map((activity) => (
                            <li key={activity.id} className="pillar-child-lecture">
                              <div className="pillar-child-lecture-header">
                                <div>
                                  <strong>{activity.title}</strong>
                                  <span className="cell-sub">
                                    +{activity.xp} XP · {activity.contentFiles.length} file
                                    {activity.contentFiles.length === 1 ? '' : 's'}
                                  </span>
                                  <StatusBadge
                                    status={activity.isActive ? 'active' : 'suspended'}
                                  />
                                </div>
                                <div className="pillar-child-actions">
                                  <label className="xp-edit-field">
                                    XP
                                    <input
                                      type="number"
                                      min={0}
                                      step={1}
                                      value={xpDrafts[activity.id] ?? String(activity.xp)}
                                      onChange={(e) =>
                                        setXpDrafts((current) => ({
                                          ...current,
                                          [activity.id]: e.target.value
                                        }))
                                      }
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    className="ghost-btn compact"
                                    disabled={
                                      childBusyId === activity.id ||
                                      parseXpInput(
                                        xpDrafts[activity.id] ?? String(activity.xp)
                                      ) === activity.xp
                                    }
                                    onClick={() =>
                                      void handleSaveChildXp('home', activity.id, activity.xp)
                                    }
                                  >
                                    Save XP
                                  </button>
                                  <label className="xp-edit-field">
                                    Badge
                                    <select
                                      value={
                                        badgeDrafts[activity.id] ?? activity.badgeId ?? ''
                                      }
                                      onChange={(e) =>
                                        setBadgeDrafts((current) => ({
                                          ...current,
                                          [activity.id]: e.target.value
                                        }))
                                      }
                                    >
                                      <option value="">No badge</option>
                                      {badges.map((badge) => (
                                        <option key={badge.id} value={badge.id}>
                                          {badge.icon ? `${badge.icon} ` : ''}
                                          {badge.name}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <button
                                    type="button"
                                    className="ghost-btn compact"
                                    disabled={
                                      childBusyId === activity.id ||
                                      (badgeDrafts[activity.id] ?? activity.badgeId ?? '') ===
                                        (activity.badgeId ?? '')
                                    }
                                    onClick={() =>
                                      void handleSaveChildBadge(
                                        'home',
                                        activity.id,
                                        activity.badgeId
                                      )
                                    }
                                  >
                                    Save badge
                                  </button>
                                  <button
                                    type="button"
                                    className="ghost-btn compact"
                                    disabled={childBusyId === activity.id}
                                    onClick={() =>
                                      void handleToggleChildActive(
                                        'home',
                                        activity.id,
                                        activity.isActive
                                      )
                                    }
                                  >
                                    {activity.isActive ? 'Deactivate' : 'Reactivate'}
                                  </button>
                                </div>
                              </div>

                              {activity.contentFiles.length > 0 ? (
                                <ul className="lecture-content-list">
                                  {activity.contentFiles.map((file) => (
                                    <li key={file.fileId}>
                                      <span>
                                        {file.originalFilename}{' '}
                                        <span className="cell-sub">({file.fileType})</span>
                                      </span>
                                      <button
                                        type="button"
                                        className="ghost-btn compact"
                                        disabled={childBusyId === activity.id}
                                        onClick={() =>
                                          void handleRemoveHomeActivityFile(
                                            activity.id,
                                            file.fileId
                                          )
                                        }
                                      >
                                        Remove
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              ) : null}

                              <div className="lecture-content-actions">
                                <label className="lecture-file-picker compact">
                                  Upload files
                                  <input
                                    type="file"
                                    multiple
                                    accept={CONTENT_FILE_ACCEPT}
                                    disabled={childBusyId === activity.id}
                                    onChange={(e) => {
                                      void handleUploadHomeActivityFiles(
                                        activity.id,
                                        e.target.files
                                      );
                                      e.target.value = '';
                                    }}
                                  />
                                  <span className="field-hint">{CONTENT_FILE_TYPES_HINT}</span>
                                </label>
                              </div>
                            </li>
                          ))
                        )}
                      </ul>
                    ) : null}
                  </div>
                </div>
              </>
            ) : null}
          </aside>
        </div>
      ) : null}

      {showCreate ? (
        <div
          className="admin-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeCreateModal();
          }}
        >
          <div
            className="admin-modal pillar-create-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={createModalTitleId}
          >
            <div className="admin-modal-header">
              <div>
                <h3 id={createModalTitleId}>Create character pillar</h3>
                <p>
                  Name, colour theme, and icon are required. Optionally add missions, lectures, and
                  home activities now.
                </p>
              </div>
              <button
                type="button"
                className="ghost-btn compact"
                onClick={closeCreateModal}
                disabled={creating}
              >
                Close
              </button>
            </div>

            <form className="admin-modal-form" onSubmit={handleCreate}>
              <div className="admin-modal-body">
                {createError ? (
                  <p className="form-error" role="alert">
                    {createError}
                  </p>
                ) : null}

                <div className="pillar-basics-grid">
                  <label>
                    Name
                    <input
                      required
                      autoFocus
                      value={createName}
                      onChange={(e) => setCreateName(e.target.value)}
                      placeholder="e.g. Respect"
                    />
                  </label>
                  <label>
                    Sort order
                    <input
                      type="number"
                      min={0}
                      value={createSortOrder}
                      onChange={(e) => setCreateSortOrder(e.target.value)}
                    />
                  </label>
                  <label>
                    Icon / emoji
                    <EmojiPickerField
                      required
                      value={createIcon}
                      onChange={setCreateIcon}
                      placeholder="Select emoji"
                    />
                  </label>
                  <label>
                    Colour theme
                    <span className="color-theme-field">
                      <input
                        type="color"
                        aria-label="Pick colour theme"
                        value={toColorInputValue(createColorTheme)}
                        onChange={(e) => setCreateColorTheme(e.target.value.toUpperCase())}
                      />
                      <input
                        required
                        value={createColorTheme}
                        onChange={(e) => setCreateColorTheme(e.target.value)}
                        placeholder="#4A90E2"
                        pattern="^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$"
                        title="Hex colour #RGB or #RRGGBB"
                      />
                    </span>
                  </label>
                  <label className="form-span-2">
                    Description
                    <textarea
                      rows={2}
                      value={createDescription}
                      onChange={(e) => setCreateDescription(e.target.value)}
                      placeholder="Optional short summary"
                    />
                  </label>
                </div>

                <div className="pillar-content-block">
                  <div className="tab-bar" role="tablist" aria-label="New pillar content">
                    <button
                      type="button"
                      className={`tab-btn${createModalTab === 'missions' ? ' active' : ''}`}
                      onClick={() => setCreateModalTab('missions')}
                    >
                      Missions ({createMissions.length})
                    </button>
                    <button
                      type="button"
                      className={`tab-btn${createModalTab === 'lectures' ? ' active' : ''}`}
                      onClick={() => setCreateModalTab('lectures')}
                    >
                      Lectures ({createLectures.length})
                    </button>
                    <button
                      type="button"
                      className={`tab-btn${createModalTab === 'home' ? ' active' : ''}`}
                      onClick={() => setCreateModalTab('home')}
                    >
                      Home ({createHomeActivities.length})
                    </button>
                  </div>

                  {createModalTab === 'missions' ? (
                    <div className="create-draft-list">
                      {createMissions.length === 0 ? (
                        <p className="muted create-draft-empty">No missions added yet.</p>
                      ) : null}
                      {createMissions.map((draft, index) => (
                        <div key={draft.key} className="create-draft-card">
                          <div className="create-draft-card-header">
                            <strong>Mission {index + 1}</strong>
                            <button
                              type="button"
                              className="ghost-btn compact"
                              onClick={() =>
                                setCreateMissions((current) =>
                                  current.filter((item) => item.key !== draft.key)
                                )
                              }
                            >
                              Remove
                            </button>
                          </div>
                          <div className="form-grid">
                            <label>
                              Name
                              <input
                                value={draft.name}
                                onChange={(e) =>
                                  setCreateMissions((current) =>
                                    current.map((item) =>
                                      item.key === draft.key
                                        ? { ...item, name: e.target.value }
                                        : item
                                    )
                                  )
                                }
                                placeholder="Mission name"
                              />
                            </label>
                            <label>
                              Description
                              <textarea
                                rows={2}
                                value={draft.description}
                                onChange={(e) =>
                                  setCreateMissions((current) =>
                                    current.map((item) =>
                                      item.key === draft.key
                                        ? { ...item, description: e.target.value }
                                        : item
                                    )
                                  )
                                }
                                placeholder="Optional"
                              />
                            </label>
                            <label>
                              Instructions
                              <textarea
                                rows={2}
                                value={draft.instructions}
                                onChange={(e) =>
                                  setCreateMissions((current) =>
                                    current.map((item) =>
                                      item.key === draft.key
                                        ? { ...item, instructions: e.target.value }
                                        : item
                                    )
                                  )
                                }
                                placeholder="Optional"
                              />
                            </label>
                            <label>
                              XP reward
                              <input
                                type="number"
                                min={0}
                                step={1}
                                value={draft.xp}
                                onChange={(e) =>
                                  setCreateMissions((current) =>
                                    current.map((item) =>
                                      item.key === draft.key
                                        ? { ...item, xp: e.target.value }
                                        : item
                                    )
                                  )
                                }
                              />
                            </label>
                            <label>
                              Badge (optional)
                              <select
                                value={draft.badgeId}
                                onChange={(e) =>
                                  setCreateMissions((current) =>
                                    current.map((item) =>
                                      item.key === draft.key
                                        ? { ...item, badgeId: e.target.value }
                                        : item
                                    )
                                  )
                                }
                              >
                                <option value="">No badge</option>
                                {badges.map((badge) => (
                                  <option key={badge.id} value={badge.id}>
                                    {badge.icon ? `${badge.icon} ` : ''}
                                    {badge.name}
                                  </option>
                                ))}
                              </select>
                            </label>
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="ghost-btn create-draft-add"
                        onClick={() =>
                          setCreateMissions((current) => [...current, emptyMission()])
                        }
                      >
                        + Add mission
                      </button>
                    </div>
                  ) : null}

                  {createModalTab === 'lectures' ? (
                    <div className="create-draft-list">
                      {createLectures.length === 0 ? (
                        <p className="muted create-draft-empty">No lectures added yet.</p>
                      ) : null}
                      {createLectures.map((draft, index) => (
                        <div key={draft.key} className="create-draft-card">
                          <div className="create-draft-card-header">
                            <strong>Lecture {index + 1}</strong>
                            <button
                              type="button"
                              className="ghost-btn compact"
                              onClick={() =>
                                setCreateLectures((current) =>
                                  current.filter((item) => item.key !== draft.key)
                                )
                              }
                            >
                              Remove
                            </button>
                          </div>
                          <div className="form-grid">
                            <label>
                              Title
                              <input
                                value={draft.title}
                                onChange={(e) =>
                                  setCreateLectures((current) =>
                                    current.map((item) =>
                                      item.key === draft.key
                                        ? { ...item, title: e.target.value }
                                        : item
                                    )
                                  )
                                }
                                placeholder="Lecture title"
                              />
                            </label>
                            <label className="form-span-2">
                              Description
                              <textarea
                                rows={2}
                                value={draft.description}
                                onChange={(e) =>
                                  setCreateLectures((current) =>
                                    current.map((item) =>
                                      item.key === draft.key
                                        ? { ...item, description: e.target.value }
                                        : item
                                    )
                                  )
                                }
                                placeholder="Optional"
                              />
                            </label>
                            <label>
                              XP reward
                              <input
                                type="number"
                                min={0}
                                step={1}
                                value={draft.xp}
                                onChange={(e) =>
                                  setCreateLectures((current) =>
                                    current.map((item) =>
                                      item.key === draft.key
                                        ? { ...item, xp: e.target.value }
                                        : item
                                    )
                                  )
                                }
                              />
                            </label>
                            <label>
                              Badge (optional)
                              <select
                                value={draft.badgeId}
                                onChange={(e) =>
                                  setCreateLectures((current) =>
                                    current.map((item) =>
                                      item.key === draft.key
                                        ? { ...item, badgeId: e.target.value }
                                        : item
                                    )
                                  )
                                }
                              >
                                <option value="">No badge</option>
                                {badges.map((badge) => (
                                  <option key={badge.id} value={badge.id}>
                                    {badge.icon ? `${badge.icon} ` : ''}
                                    {badge.name}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <div className="form-span-2 lecture-attachments">
                              <div className="lecture-attachments-header">
                                <span>Files &amp; video links</span>
                                <button
                                  type="button"
                                  className="ghost-btn compact"
                                  onClick={() =>
                                    setCreateLectures((current) =>
                                      current.map((item) =>
                                        item.key === draft.key
                                          ? {
                                              ...item,
                                              links: [...item.links, emptyLectureLink()]
                                            }
                                          : item
                                      )
                                    )
                                  }
                                >
                                  + Add video link
                                </button>
                              </div>
                              {draft.links.map((link) => (
                                <div key={link.key} className="lecture-link-draft">
                                  <input
                                    type="url"
                                    value={link.url}
                                    onChange={(e) =>
                                      setCreateLectures((current) =>
                                        current.map((item) =>
                                          item.key === draft.key
                                            ? {
                                                ...item,
                                                links: item.links.map((entry) =>
                                                  entry.key === link.key
                                                    ? { ...entry, url: e.target.value }
                                                    : entry
                                                )
                                              }
                                            : item
                                        )
                                      )
                                    }
                                    placeholder="https://… video URL"
                                  />
                                  <input
                                    value={link.label}
                                    onChange={(e) =>
                                      setCreateLectures((current) =>
                                        current.map((item) =>
                                          item.key === draft.key
                                            ? {
                                                ...item,
                                                links: item.links.map((entry) =>
                                                  entry.key === link.key
                                                    ? { ...entry, label: e.target.value }
                                                    : entry
                                                )
                                              }
                                            : item
                                        )
                                      )
                                    }
                                    placeholder="Label (optional)"
                                  />
                                  <button
                                    type="button"
                                    className="ghost-btn compact"
                                    onClick={() =>
                                      setCreateLectures((current) =>
                                        current.map((item) =>
                                          item.key === draft.key
                                            ? {
                                                ...item,
                                                links: item.links.filter(
                                                  (entry) => entry.key !== link.key
                                                )
                                              }
                                            : item
                                        )
                                      )
                                    }
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                              <label className="lecture-file-picker">
                                Upload files
                                <input
                                  type="file"
                                  multiple
                                  accept={CONTENT_FILE_ACCEPT}
                                  onChange={(e) => {
                                    const nextFiles = Array.from(e.target.files ?? []);
                                    if (nextFiles.length === 0) return;
                                    setCreateLectures((current) =>
                                      current.map((item) =>
                                        item.key === draft.key
                                          ? { ...item, files: [...item.files, ...nextFiles] }
                                          : item
                                      )
                                    );
                                    e.target.value = '';
                                  }}
                                />
                                <span className="field-hint">{CONTENT_FILE_TYPES_HINT}</span>
                              </label>
                              {draft.files.length > 0 ? (
                                <ul className="lecture-pending-list">
                                  {draft.files.map((file, fileIndex) => (
                                    <li key={`${file.name}-${file.size}-${fileIndex}`}>
                                      <span>
                                        {file.name}{' '}
                                        <span className="cell-sub">
                                          ({Math.round(file.size / 1024)} KB)
                                        </span>
                                      </span>
                                      <button
                                        type="button"
                                        className="ghost-btn compact"
                                        onClick={() =>
                                          setCreateLectures((current) =>
                                            current.map((item) =>
                                              item.key === draft.key
                                                ? {
                                                    ...item,
                                                    files: item.files.filter(
                                                      (_, i) => i !== fileIndex
                                                    )
                                                  }
                                                : item
                                            )
                                          )
                                        }
                                      >
                                        Remove
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="muted create-draft-empty">
                                  No files selected. They upload to S3 after the pillar is created.
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="ghost-btn create-draft-add"
                        onClick={() =>
                          setCreateLectures((current) => [...current, emptyLecture()])
                        }
                      >
                        + Add lecture
                      </button>
                    </div>
                  ) : null}

                  {createModalTab === 'home' ? (
                    <div className="create-draft-list">
                      {createHomeActivities.length === 0 ? (
                        <p className="muted create-draft-empty">No home activities added yet.</p>
                      ) : null}
                      {createHomeActivities.map((draft, index) => (
                        <div key={draft.key} className="create-draft-card">
                          <div className="create-draft-card-header">
                            <strong>Home activity {index + 1}</strong>
                            <button
                              type="button"
                              className="ghost-btn compact"
                              onClick={() =>
                                setCreateHomeActivities((current) =>
                                  current.filter((item) => item.key !== draft.key)
                                )
                              }
                            >
                              Remove
                            </button>
                          </div>
                          <div className="form-grid">
                            <label>
                              Title
                              <input
                                value={draft.title}
                                onChange={(e) =>
                                  setCreateHomeActivities((current) =>
                                    current.map((item) =>
                                      item.key === draft.key
                                        ? { ...item, title: e.target.value }
                                        : item
                                    )
                                  )
                                }
                                placeholder="Activity title"
                              />
                            </label>
                            <label>
                              Description
                              <textarea
                                rows={2}
                                value={draft.description}
                                onChange={(e) =>
                                  setCreateHomeActivities((current) =>
                                    current.map((item) =>
                                      item.key === draft.key
                                        ? { ...item, description: e.target.value }
                                        : item
                                    )
                                  )
                                }
                                placeholder="Optional"
                              />
                            </label>
                            <label>
                              Instructions
                              <textarea
                                rows={2}
                                value={draft.instructions}
                                onChange={(e) =>
                                  setCreateHomeActivities((current) =>
                                    current.map((item) =>
                                      item.key === draft.key
                                        ? { ...item, instructions: e.target.value }
                                        : item
                                    )
                                  )
                                }
                                placeholder="Optional"
                              />
                            </label>
                            <label>
                              XP reward
                              <input
                                type="number"
                                min={0}
                                step={1}
                                value={draft.xp}
                                onChange={(e) =>
                                  setCreateHomeActivities((current) =>
                                    current.map((item) =>
                                      item.key === draft.key
                                        ? { ...item, xp: e.target.value }
                                        : item
                                    )
                                  )
                                }
                              />
                            </label>
                            <label>
                              Badge (optional)
                              <select
                                value={draft.badgeId}
                                onChange={(e) =>
                                  setCreateHomeActivities((current) =>
                                    current.map((item) =>
                                      item.key === draft.key
                                        ? { ...item, badgeId: e.target.value }
                                        : item
                                    )
                                  )
                                }
                              >
                                <option value="">No badge</option>
                                {badges.map((badge) => (
                                  <option key={badge.id} value={badge.id}>
                                    {badge.icon ? `${badge.icon} ` : ''}
                                    {badge.name}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <div className="lecture-content-actions create-draft-full">
                              <label className="lecture-file-picker">
                                Upload files
                                <input
                                  type="file"
                                  multiple
                                  accept={CONTENT_FILE_ACCEPT}
                                  onChange={(e) => {
                                    const nextFiles = Array.from(e.target.files ?? []);
                                    if (nextFiles.length === 0) return;
                                    setCreateHomeActivities((current) =>
                                      current.map((item) =>
                                        item.key === draft.key
                                          ? { ...item, files: [...item.files, ...nextFiles] }
                                          : item
                                      )
                                    );
                                    e.target.value = '';
                                  }}
                                />
                                <span className="field-hint">{CONTENT_FILE_TYPES_HINT}</span>
                              </label>
                              {draft.files.length > 0 ? (
                                <ul className="lecture-pending-list">
                                  {draft.files.map((file, fileIndex) => (
                                    <li key={`${file.name}-${file.size}-${fileIndex}`}>
                                      <span>
                                        {file.name}{' '}
                                        <span className="cell-sub">
                                          ({Math.round(file.size / 1024)} KB)
                                        </span>
                                      </span>
                                      <button
                                        type="button"
                                        className="ghost-btn compact"
                                        onClick={() =>
                                          setCreateHomeActivities((current) =>
                                            current.map((item) =>
                                              item.key === draft.key
                                                ? {
                                                    ...item,
                                                    files: item.files.filter(
                                                      (_, i) => i !== fileIndex
                                                    )
                                                  }
                                                : item
                                            )
                                          )
                                        }
                                      >
                                        Remove
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="muted create-draft-empty">
                                  No files selected. They upload to S3 after the pillar is created.
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="ghost-btn create-draft-add"
                        onClick={() =>
                          setCreateHomeActivities((current) => [
                            ...current,
                            emptyHomeActivity()
                          ])
                        }
                      >
                        + Add home activity
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="admin-modal-actions">
                <button type="submit" className="primary-btn" disabled={creating}>
                  {creating ? 'Creating…' : 'Create pillar'}
                </button>
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={closeCreateModal}
                  disabled={creating}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </section>
  );
}
